import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild, ViewContainerRef } from "@angular/core";
import { ApplicationLinkService } from "src/app/common/services/application-link.service";
import { ApplicationLinkCode } from "src/app/common/services/application-link-codes";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { AuthService, PredefinedUserZone } from "@saliente/common-auth";
import { dateToInt } from "@saliente/library";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { MicroProfitFormModel } from "../client-forms.models";
import { ClientFormsService } from "../client-forms.service";

@Component({
    selector: 'micro-profit-form',
    templateUrl: 'micro-profit-form.component.html',
    styleUrls: ['micro-profit-form.component.scss'],
    standalone: false
})
export class MicroProfitFormComponent implements OnInit, OnDestroy {
    @Input()
    public isDialog: boolean;
    @Input()
    public clientEidParam: string;
    @Input()
    public instanceParam: string;

    @Output() public close = new EventEmitter();

    //subscriptions
	private microProfitDataSubscription: Subscription;
    
    //models
    public microProfitFormModel: MicroProfitFormModel;

    public clientEid: string;
    public instance: string;

    //booleans
    public isReadOnly: boolean;
    public hasViewRight: boolean;
    public isLoading: boolean;
    public showConfirmationDialog: boolean = false;

    get areConsultanta80Disabled(): boolean {
        return this.microProfitFormModel.data.are1FTE == null || !this.microProfitFormModel.data.are1FTE;
    }

    get are253MicroDisabled(): boolean {
        return this.microProfitFormModel.data.areConsultanta80 == null || this.microProfitFormModel.data.areConsultanta80;
    }

    get pastreazaFirmaMicroDisabled(): boolean {
        return this.microProfitFormModel.data.are253Micro == null || !this.microProfitFormModel.data.are253Micro;
    }

    get saveDisabled(): boolean {
        return this.microProfitFormModel.data.ramaneMicro == null;
    }

    get isFormExpired(): boolean {
        if (!this.microProfitFormModel?.formData?.expires) return false;
        return dateToInt(new Date()) > this.microProfitFormModel.formData.expires;
    }

    constructor(private clientFormsService: ClientFormsService, private authService: AuthService, private route: ActivatedRoute, private toastr: ToastrService, private applicationLinkService: ApplicationLinkService,
            private cdr: ChangeDetectorRef,
            @Optional() @Inject('instance') public instanceRouteParam: string) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.microProfitDataSubscription = this.route.paramMap
            .pipe(
                switchMap((params: ParamMap) => {
                    this.clientEid = this.isDialog ? this.clientEidParam : params.get('id');
                    this.instance = this.isDialog ? this.instanceParam : this.instanceRouteParam;

                    return this.clientFormsService.getMicroProfitData(this.clientEid, this.instance);
                })
		    )
			.subscribe((microProfitFormData: MicroProfitFormModel) => {
                if (microProfitFormData) {
                    this.isReadOnly = this.authService.selectedZoneCode == PredefinedUserZone.Expert
                        || microProfitFormData.formData.status == 'Submitted'
                        || (!!microProfitFormData.formData.expires && dateToInt(new Date()) > microProfitFormData.formData.expires);

                    this.hasViewRight = this.authService.isInRole('EXPERT') || this.authService.isInRole('ZEUS') 
                        || this.authService.isInRole(microProfitFormData.formData.displayRole);

                    if (this.hasViewRight) {
                        this.microProfitFormModel = microProfitFormData;

                        if (this.microProfitFormModel.formData.status != 'Submitted') {
                            let ramaneMicro = microProfitFormData.data.ramaneMicro;
                            this.computeBooleans();
                            this.microProfitFormModel.data.ramaneMicro = ramaneMicro;
                        }

                        this.getMicroProfitFormConclusion();
                    }
                }
                else {
                    this.microProfitFormModel = null;
                }

                this.isLoading = false;

                setTimeout(() => {
                    this.cdr.detectChanges();
                }, 200);
		});
    }

    ngOnDestroy(): void {
        if (this.microProfitDataSubscription) { this.microProfitDataSubscription.unsubscribe(); this.microProfitDataSubscription = null; }
    }

    public onAre1FTEHelp() {
        this.applicationLinkService.open(ApplicationLinkCode.M2pAngajat);
    }

    public onAreConsultanta80() {
        this.applicationLinkService.open(ApplicationLinkCode.Consultanta80);
    }

    public onAre253Micro() {
        this.applicationLinkService.open(ApplicationLinkCode.M2pPlafon);
    }

    public onRamaneMicroHelp() {
        const code = this.microProfitFormModel.data.ramaneMicro ? ApplicationLinkCode.ImpozitVenit : ApplicationLinkCode.ImpozitProfit;
        this.applicationLinkService.open(code);
    }

    public computeBooleans() {
        this.onAre1FTEChange();
        this.onAreConsultanta80Change();
        this.onAre253MicroChange();
        this.onPastreazaFirmaMicroChange();
    }

    public onAre1FTEChange() {
        if (this.microProfitFormModel.data.are1FTE == null) {
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else if (this.microProfitFormModel.data.are1FTE) { // answer: yes - need to answer the following questions
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else { // answer: no - there is no need for the other questions: !ramaneMicro
            this.microProfitFormModel.data.areConsultanta80 = null;
            this.microProfitFormModel.data.are253Micro = null;
            this.microProfitFormModel.data.pastreazaFirmaMicro = null;

            this.microProfitFormModel.data.ramaneMicro = false;
        }
    }

    public onAreConsultanta80Change() {
        if (this.microProfitFormModel.data.areConsultanta80 == null) {
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else if (this.microProfitFormModel.data.areConsultanta80) { // answer: yes - there is no need for the following questions: !ramaneMicro
            this.microProfitFormModel.data.are253Micro = null;
            this.microProfitFormModel.data.pastreazaFirmaMicro = null;
            
            this.microProfitFormModel.data.ramaneMicro = false;
        }
        else { // answer: no - need to answer the following questions
            this.microProfitFormModel.data.ramaneMicro = null;
        }
    }

    public onAre253MicroChange() {
        if (this.microProfitFormModel.data.are253Micro == null) {
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else if (this.microProfitFormModel.data.are253Micro) { // answer: yes - need to answer the following questions
            this.microProfitFormModel.data.pastreazaFirmaMicro = null;
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else { // answer: no - there is no need for the following questions: ramaneMicro
            this.microProfitFormModel.data.ramaneMicro = true;
            this.microProfitFormModel.data.pastreazaFirmaMicro = null;
        }
    }

    public onPastreazaFirmaMicroChange() {
        if (this.microProfitFormModel.data.pastreazaFirmaMicro == null) {
            this.microProfitFormModel.data.ramaneMicro = null;
        }
        else if (this.microProfitFormModel.data.pastreazaFirmaMicro) { // answer: yes - ramaneMicro
            this.microProfitFormModel.data.ramaneMicro = true;
        }
        else { // answer: no - !ramaneMicro
            this.microProfitFormModel.data.ramaneMicro = false;
        }
    }
    
    public getMicroProfitFormConclusion() {
        return this.microProfitFormModel.data.ramaneMicro
            ? 'Firma va rămâne Microîntreprindere - plătitoare de impozit pe venit.' 
            : 'Firma va deveni plătitoare de impozit pe profit.'
    }

    public openConfirmationDialog() {
        this.showConfirmationDialog = true;
    }

    public closeConfirmationDialog() {
        this.showConfirmationDialog = false;
    }

    public async saveMicroProfitForm() {
        let result = await this.clientFormsService.updateClientFormMicroProfitData(this.clientEid, this.instance, this.microProfitFormModel.data);
        if (result) {
            if (!this.isDialog) {
                this.microProfitFormModel.formData.status = "Submitted";
                this.isReadOnly = this.authService.selectedZoneCode == PredefinedUserZone.Expert || this.microProfitFormModel.formData.status == 'Submitted';
            }
            
            this.cancelMicroProfitForm();
            this.toastr.success("Formularul a fost salvat cu succes!");
        }
    }

    public cancelMicroProfitForm() {
        this.closeConfirmationDialog();

        if (this.isDialog) {
            this.close.emit();
        }
    }
}
